import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import {
  Box,
  Button,
  Heading,
  ListItem,
  SimpleGrid,
  Text,
  UnorderedList,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import type {
  CsvImportProfile,
  CustomCsvWizardState,
  LayoutRowGroupingId,
  LayoutShape,
  LayoutShapeId,
} from "../../../types/customCsvImport";
import type { CustomCsvImportCapabilities } from "../../../services/brokerIntegrationApi";
import CsvLayoutExampleTable from "./visuals/CsvLayoutExampleTable";
import {
  findLayoutShapesForGrouping,
  getGroupingPreviewShape,
  isPerFillShape,
  layoutShapeExampleRows,
  mergeLayoutShapeIntoState,
  resolveFieldLabels,
  resolveLayoutRowGroupings,
  resolveLayoutShapes,
} from "../../../utils/layoutShapes";
import {
  getMappableFieldsForMode,
  mergeProfileIntoState,
} from "../../../utils/customCsvImport";

type LayoutPickView = "grouping" | "shape" | "confirm";

export type CustomCsvLayoutPickStepHandle = {
  canContinue: () => boolean;
  handleBack: () => boolean;
  handleContinue: () => boolean;
  syncViewFromState: () => void;
  isAtFirstView: () => boolean;
};

export interface CustomCsvLayoutPickStepProps {
  csvImport: CustomCsvImportCapabilities;
  state: CustomCsvWizardState;
  templates: CsvImportProfile[];
  onStateChange: (patch: Partial<CustomCsvWizardState>) => void;
  onSkipToUpload: () => void;
  onViewChange?: () => void;
}

function LayoutOptionCardField({
  title,
  description,
}: {
  title: string;
  description?: string | null;
}): React.ReactElement {
  return (
    <Field className="pointer-events-none w-full min-w-0 gap-1 text-left">
      <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{title}</FieldLabel>
      {description ? (
        <FieldDescription className={BROKER_ALERT_DESC_CLASS}>{description}</FieldDescription>
      ) : null}
    </Field>
  );
}

function RequiredFieldsNote({
  shape,
  mappableFields,
}: {
  shape: LayoutShape;
  mappableFields: ReturnType<typeof getMappableFieldsForMode>;
}): React.ReactElement {
  const labels = resolveFieldLabels(shape.required_fields, mappableFields);
  const hasSymbol = shape.required_fields.includes("symbol");

  return (
    <Field className="pointer-events-none mt-3 w-full min-w-0 gap-1 text-left">
      <FieldLabel className={cn(BROKER_ALERT_TITLE_CLASS, "text-xs")}>
        Required Tessera fields
      </FieldLabel>
      <FieldDescription className={cn(BROKER_ALERT_DESC_CLASS, "text-xs leading-5")}>
        Map these using your file&apos;s column names:
      </FieldDescription>
      <UnorderedList
        className={cn(BROKER_ALERT_DESC_CLASS, "m-0 list-disc pl-4 text-xs leading-5")}
        spacing={0.5}
      >
        {labels.map((label) => (
          <ListItem key={label}>{label}</ListItem>
        ))}
      </UnorderedList>
      {hasSymbol ? (
        <FieldDescription className={cn(BROKER_ALERT_DESC_CLASS, "text-xs leading-5")}>
          Or provide one symbol for the whole file if your export has no symbol column.
        </FieldDescription>
      ) : null}
    </Field>
  );
}

function ShapeExampleTable({
  shape,
  caption,
}: {
  shape: LayoutShape;
  caption?: string;
}): React.ReactElement {
  const minRows = shape.row_grouping_id === "paired_rows" ? 2 : 1;

  return (
    <Box mt={3}>
      {caption ? (
        <FieldDescription className={cn(BROKER_ALERT_DESC_CLASS, "mb-1 text-xs leading-5")}>
          {caption}
        </FieldDescription>
      ) : null}
      <CsvLayoutExampleTable
        headers={shape.example_headers}
        rows={layoutShapeExampleRows(shape)}
        compact
        minBodyRows={minRows}
      />
    </Box>
  );
}

function LayoutConfirmPanel({
  shape,
  mappableFields,
}: {
  shape: LayoutShape;
  mappableFields: ReturnType<typeof getMappableFieldsForMode>;
}): React.ReactElement {
  const muted = useColorModeValue("gray.600", "gray.400");

  return (
    <Box pt={2}>
      <Text fontSize="sm" color={muted} mb={2}>
        Your file should follow this structure. Next you will upload your CSV and map each required
        field to your column names.
      </Text>
      {isPerFillShape(shape) ? (
        <Text fontSize="sm" color={muted} mb={2}>
          Each row in your file is one fill, not two rows per completed trade.
        </Text>
      ) : null}
      <Field className="mb-2 w-full min-w-0 gap-1">
        <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{shape.label}</FieldLabel>
      </Field>
      <CsvLayoutExampleTable
        headers={shape.example_headers}
        rows={layoutShapeExampleRows(shape)}
        compact
        minBodyRows={shape.row_grouping_id === "paired_rows" ? 2 : 1}
      />
      <RequiredFieldsNote shape={shape} mappableFields={mappableFields} />
    </Box>
  );
}

function resolveViewFromState(
  rowGroupingId: LayoutRowGroupingId | "",
  layoutShapeId: LayoutShapeId | "",
  needsShapeView: boolean
): LayoutPickView {
  if (layoutShapeId && needsShapeView) return "confirm";
  if (rowGroupingId && needsShapeView && !layoutShapeId) return "shape";
  return "grouping";
}

const CustomCsvLayoutPickStep = forwardRef<
  CustomCsvLayoutPickStepHandle,
  CustomCsvLayoutPickStepProps
>(function CustomCsvLayoutPickStep(
  { csvImport, state, templates, onStateChange, onSkipToUpload, onViewChange },
  ref
): React.ReactElement {
  const muted = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const selectedBg = useColorModeValue("cyan.50", "whiteAlpha.100");
  const rowGroupings = resolveLayoutRowGroupings(csvImport);
  const allShapes = resolveLayoutShapes(csvImport);

  const [rowGroupingId, setRowGroupingId] = useState<LayoutRowGroupingId | "">(() => {
    if (!state.layoutShapeId) return "";
    const shape = allShapes.find((s) => s.id === state.layoutShapeId);
    return shape?.row_grouping_id ?? "";
  });
  const [layoutShapeId, setLayoutShapeId] = useState<LayoutShapeId | "">(
    () => (state.layoutShapeId as LayoutShapeId) ?? ""
  );

  const shapeOptions = useMemo(
    () => (rowGroupingId ? findLayoutShapesForGrouping(csvImport, rowGroupingId) : []),
    [csvImport, rowGroupingId]
  );
  const needsShapeView = shapeOptions.length > 1;

  const [view, setView] = useState<LayoutPickView>(() =>
    resolveViewFromState(rowGroupingId, layoutShapeId, needsShapeView)
  );

  const selectedShape = useMemo(
    () => allShapes.find((s) => s.id === layoutShapeId),
    [allShapes, layoutShapeId]
  );

  const mappableFields = getMappableFieldsForMode(
    csvImport,
    selectedShape?.import_mode ?? state.importMode
  );

  const setViewWithNotify = (next: LayoutPickView) => {
    setView(next);
    onViewChange?.();
  };

  const syncViewFromState = () => {
    setViewWithNotify(resolveViewFromState(rowGroupingId, layoutShapeId, needsShapeView));
  };

  const selectGrouping = (id: LayoutRowGroupingId) => {
    setRowGroupingId(id);
    setLayoutShapeId("");
    const shapes = findLayoutShapesForGrouping(csvImport, id);
    if (shapes.length === 1) {
      setLayoutShapeId(shapes[0].id);
      onStateChange(mergeLayoutShapeIntoState(shapes[0]));
      setViewWithNotify("grouping");
      return;
    }
    onStateChange({
      startSource: "layout",
      profileSlug: undefined,
      layoutShapeId: undefined,
      columnMapping: {},
      preview: null,
      acknowledgedWarningCodes: new Set(),
    });
    setViewWithNotify("grouping");
  };

  const selectShape = (id: LayoutShapeId) => {
    setLayoutShapeId(id);
    const shape = allShapes.find((s) => s.id === id);
    if (shape) {
      onStateChange(mergeLayoutShapeIntoState(shape));
    }
  };

  const selectProfile = (profile: CsvImportProfile) => {
    onStateChange(mergeProfileIntoState(profile));
    onSkipToUpload();
  };

  const showConfirmPanel =
    Boolean(selectedShape) &&
    (view === "confirm" || (view === "grouping" && !needsShapeView));

  const canContinue = (): boolean => {
    switch (view) {
      case "grouping":
        if (!needsShapeView) {
          return Boolean(selectedShape);
        }
        return Boolean(rowGroupingId);
      case "shape":
        return Boolean(layoutShapeId);
      case "confirm":
        return Boolean(selectedShape);
      default:
        return false;
    }
  };

  const handleContinue = (): boolean => {
    if (view === "grouping") {
      if (!rowGroupingId) return true;
      if (!needsShapeView && selectedShape) {
        return false;
      }
      if (needsShapeView) {
        setViewWithNotify("shape");
        return true;
      }
      return true;
    }
    if (view === "shape") {
      if (!layoutShapeId) return true;
      setViewWithNotify("confirm");
      return true;
    }
    return false;
  };

  const handleBack = (): boolean => {
    if (view === "confirm") {
      setViewWithNotify(needsShapeView ? "shape" : "grouping");
      return true;
    }
    if (view === "shape") {
      setViewWithNotify("grouping");
      return true;
    }
    return false;
  };

  useImperativeHandle(ref, () => ({
    canContinue,
    handleBack,
    handleContinue,
    syncViewFromState,
    isAtFirstView: () => view === "grouping",
  }));

  const groupingCaption = (id: LayoutRowGroupingId): string => {
    if (id === "single_row") return "Example: one CSV row = one completed trade";
    if (id === "paired_rows") return "Example: two CSV rows share the same Trade ID";
    return "Example: one CSV row = one fill (not one completed trade)";
  };

  return (
    <VStack align="stretch" spacing={6}>
      {view === "grouping" && templates.length > 0 ? (
        <Box>
          <Heading size="sm" mb={2}>
            Saved templates
          </Heading>
          <VStack align="stretch" spacing={2}>
            {templates.map((template) => (
              <Button
                key={template.profile_slug}
                variant="outline"
                justifyContent="flex-start"
                onClick={() => selectProfile(template)}
              >
                {template.display_name}
              </Button>
            ))}
          </VStack>
        </Box>
      ) : null}

      <Box>
        {view === "grouping" ? (
          <Text fontSize="sm" color={muted} mb={3}>
            Pick the pattern that matches your file. Column names in the examples are illustrative
            only. You will map your own headers after upload.
          </Text>
        ) : null}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={3} alignItems="stretch">
          {rowGroupings.map((grouping) => {
            const selected = rowGroupingId === grouping.id;
            const previewShape = getGroupingPreviewShape(csvImport, grouping.id);
            return (
              <Box
                key={grouping.id}
                as="button"
                type="button"
                textAlign="left"
                borderWidth="2px"
                borderColor={selected ? "cyan.400" : borderColor}
                borderRadius="lg"
                bg={selected ? selectedBg : undefined}
                p={4}
                h="100%"
                display="flex"
                flexDirection="column"
                onClick={() => selectGrouping(grouping.id)}
              >
                <LayoutOptionCardField
                  title={grouping.label}
                  description={grouping.description}
                />
                {previewShape ? (
                  <ShapeExampleTable
                    shape={previewShape}
                    caption={groupingCaption(grouping.id)}
                  />
                ) : null}
              </Box>
            );
          })}
        </SimpleGrid>
      </Box>

      {view === "shape" && rowGroupingId ? (
        <Box>
          <Text fontSize="sm" color={muted} mb={3}>
            Same row count as above. Choose how your file encodes buy/sell and open/close.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} alignItems="stretch">
            {shapeOptions.map((shape) => {
              const selected = layoutShapeId === shape.id;
              const shapeMappable = getMappableFieldsForMode(csvImport, shape.import_mode);
              return (
                <Box
                  key={shape.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  borderWidth="2px"
                  borderColor={selected ? "cyan.400" : borderColor}
                  borderRadius="lg"
                  bg={selected ? selectedBg : undefined}
                  p={4}
                  h="100%"
                  display="flex"
                  flexDirection="column"
                  onClick={() => selectShape(shape.id)}
                >
                  <LayoutOptionCardField title={shape.label} description={shape.description} />
                  <ShapeExampleTable shape={shape} />
                  <RequiredFieldsNote shape={shape} mappableFields={shapeMappable} />
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>
      ) : null}

      {showConfirmPanel && selectedShape ? (
        <LayoutConfirmPanel shape={selectedShape} mappableFields={mappableFields} />
      ) : null}
    </VStack>
  );
});

export default CustomCsvLayoutPickStep;
